import React, { useState } from 'react';
import useSWR from 'swr';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import api from '../../services/api';
import { fetcher } from '../../services/fetcher';
import { cn } from '../../lib/utils';
import { formatDate } from '../../config/appConfig';
import {
  Badge,
  Button,
  Card,
  Label,
  Select,
  type SelectOption,
  Textarea,
  DateInput,
  DateTimeInput,
  ConfirmationDialog,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
  XIcon,
} from '../../components/ui';
import { renderRenewalStatusBadge } from '../service-renewal-reminders/config';

const API = '/client-service-renewal-reminders';
export interface RenewalReminder {
  id: number;
  clientServiceId: number;
  sentAt: string;
  reminderNumber: number | null;
  channel: string | null;
  emailId: number | null;
  renewalResponseStatus: string | null;
  renewalResponseNote: string | null;
  renewalResponseAt: string | null;
  createdById: number | null;
  updatedById: number | null;
  createdAt: string | null;
  updatedAt: string | null;
  createdBy?: { userFirstName?: string; userLastName?: string };
  updatedBy?: { userFirstName?: string; userLastName?: string };
}

const STATUS_SELECT_OPTIONS: SelectOption[] = [
  { value: '', label: '—' },
  { value: 'pending', label: 'Pending' },
  { value: 'renewed', label: 'Renewed' },
  { value: 'will_proceed', label: 'Will proceed' },
  { value: 'declined', label: 'Declined' },
  { value: 'no_response', label: 'No response' },
];
const CHANNEL_SELECT_OPTIONS: SelectOption[] = [
  { value: 'email', label: 'Email' },
  { value: 'phone', label: 'Phone' },
  { value: 'other', label: 'Other' },
];

interface RenewalRemindersSectionProps {
  clientServiceId: number;
  /** Called after add/update/delete so parent can refetch (e.g. list counts) */
  onReminderAdded?: () => void;
  /** panel = card layout + sections; inline = compact table (e.g. detail page) */
  variant?: 'inline' | 'panel';
}

export const RenewalRemindersSection: React.FC<RenewalRemindersSectionProps> = ({ clientServiceId, onReminderAdded, variant = 'inline' }) => {
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const { data: reminders = [], mutate } = useSWR<RenewalReminder[]>(
    `${API}?clientServiceId=${clientServiceId}`,
    fetcher
  );
  const [adding, setAdding] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [form, setForm] = useState({
    sentAt: new Date().toISOString(),
    channel: 'email',
  });
  const [editForm, setEditForm] = useState({
    renewalResponseStatus: '',
    renewalResponseNote: '',
    renewalResponseAt: '',
  });

  const statusLabel = (value: string | null) => STATUS_SELECT_OPTIONS.find((o) => o.value === value)?.label || '—';

  const handleAdd = async () => {
    try {
      const nextNumber = reminders.length + 1;
      await api.post(API, {
        clientServiceId,
        sentAt: new Date(form.sentAt).toISOString(),
        reminderNumber: nextNumber,
        channel: form.channel || undefined,
        renewalResponseStatus: 'pending',
      });
      showSuccess('Reminder logged.');
      setAdding(false);
      setForm({ sentAt: new Date().toISOString(), channel: 'email' });
      mutate();
      onReminderAdded?.();
    } catch (e: any) {
      showError(e.response?.data?.message || e.message || 'Failed to add reminder');
    }
  };

  const openEdit = (r: RenewalReminder) => {
    setEditingId(r.id);
    setEditForm({
      renewalResponseStatus: r.renewalResponseStatus || '',
      renewalResponseNote: r.renewalResponseNote || '',
      renewalResponseAt: r.renewalResponseAt ? r.renewalResponseAt.slice(0, 10) : '',
    });
  };

  const handleUpdate = async () => {
    if (editingId == null) return;
    try {
      await api.patch(`${API}/${editingId}`, {
        renewalResponseStatus: editForm.renewalResponseStatus || undefined,
        renewalResponseNote: editForm.renewalResponseNote || undefined,
        renewalResponseAt: editForm.renewalResponseAt ? new Date(editForm.renewalResponseAt).toISOString() : undefined,
      });
      showSuccess('Response updated.');
      setEditingId(null);
      mutate();
      onReminderAdded?.();
    } catch (e: any) {
      showError(e.response?.data?.message || e.message || 'Failed to update');
    }
  };

  const handleDeleteClick = (id: number) => setDeleteConfirmId(id);
  const handleDeleteConfirm = async () => {
    if (deleteConfirmId == null) return;
    try {
      await api.delete(`${API}/${deleteConfirmId}`);
      showSuccess('Reminder deleted.');
      setDeleteConfirmId(null);
      mutate();
      onReminderAdded?.();
    } catch (e: any) {
      showError(e.response?.data?.message || e.message || 'Failed to delete');
    }
  };

  const formBlock = (
    <Card variant="outlined" className={cn('p-4', variant === 'panel' && 'mb-6')}>
      <div className="space-y-3 mb-4">
        <div>
          <Label className="text-xs uppercase tracking-wide mb-1">Sent at</Label>
          <DateTimeInput
            value={form.sentAt}
            onChange={(e) => setForm((f) => ({ ...f, sentAt: e.target.value }))}
            showClearButton={false}
          />
        </div>
        <div>
          <Label className="text-xs uppercase tracking-wide mb-1">Channel</Label>
          <Select
            value={form.channel}
            onChange={(e) => setForm((f) => ({ ...f, channel: e.target.value }))}
            options={CHANNEL_SELECT_OPTIONS}
            placeholder="Channel"
          />
        </div>
      </div>
      <div className="flex gap-2">
        <Button type="button" onClick={handleAdd} variant="default">
          <PlusIcon className="w-4 h-4" />
          Save reminder
        </Button>
        <Button type="button" variant="outline" onClick={() => setAdding(false)}>
          Cancel
        </Button>
      </div>
    </Card>
  );

  if (variant === 'panel') {
    return (
      <>
      <div className="space-y-6">
        <section className="space-y-3">
          <h3 className={cn('text-sm font-semibold uppercase tracking-wide', colors.text)}>Log reminder</h3>
          {adding ? (
            formBlock
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => setAdding(true)}
              className="w-full py-4 border-2 border-dashed"
            >
              <PlusIcon className="w-5 h-5" />
              Log reminder
            </Button>
          )}
        </section>

        <section className="space-y-3">
          <h3 className={cn('text-sm font-semibold uppercase tracking-wide', colors.text)}>
            Reminders ({reminders.length})
          </h3>
          {reminders.length === 0 && !adding ? (
            <Card variant="outlined" className={cn('rounded-xl border-dashed p-6 text-center text-sm', colors.textMuted)}>
              <p>No reminders yet. Log one when you send a renewal notification.</p>
            </Card>
          ) : (
            <ul className="space-y-3">
              {reminders.map((r) => (
                <li key={r.id}>
                  <Card variant="default" className="p-4 shadow-sm">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="flex flex-wrap items-center gap-2 min-w-0">
                        <span className={cn('text-sm font-medium', colors.text)}>{formatDate(r.sentAt)}</span>
                        <span className={cn('text-xs', colors.textMuted)}>#{r.reminderNumber ?? '—'}</span>
                        {r.channel && <Badge variant="secondary">{String(r.channel)}</Badge>}
                        {r.renewalResponseStatus && renderRenewalStatusBadge(r.renewalResponseStatus)}
                      </div>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        {editingId === r.id ? (
                          <>
                            <button
                              type="button"
                              onClick={handleUpdate}
                              className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                              title="Save"
                              aria-label="Save"
                            >
                              <CheckIcon className="w-5 h-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              title="Cancel"
                              aria-label="Cancel"
                            >
                              <XIcon className="w-5 h-5" />
                            </button>
                          </>
                        ) : (
                          <>
                            <button
                              type="button"
                              onClick={() => openEdit(r)}
                              className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                              title="Edit response"
                              aria-label="Edit response"
                            >
                              <PencilIcon className="w-5 h-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeleteClick(r.id)}
                              className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                              title="Delete"
                              aria-label="Delete"
                            >
                              <TrashIcon className="w-5 h-5" />
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                    {editingId === r.id ? (
                      <div className={cn('space-y-3 pt-2 border-t', colors.border)}>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                          <div>
                            <Label className="text-xs mb-1">Response date</Label>
                            <DateInput
                              value={editForm.renewalResponseAt}
                              onChange={(e) => setEditForm((f) => ({ ...f, renewalResponseAt: e.target.value }))}
                              showClearButton={false}
                            />
                          </div>
                          <div>
                            <Label className="text-xs mb-1">Response status</Label>
                            <Select
                              value={editForm.renewalResponseStatus}
                              onChange={(e) => setEditForm((f) => ({ ...f, renewalResponseStatus: e.target.value }))}
                              options={STATUS_SELECT_OPTIONS}
                              placeholder="Status"
                            />
                          </div>
                        </div>
                        <div>
                          <Label className="text-xs mb-1">Response note</Label>
                          <Textarea
                            value={editForm.renewalResponseNote}
                            onChange={(e) => setEditForm((f) => ({ ...f, renewalResponseNote: e.target.value }))}
                            rows={2}
                            placeholder="Optional note (e.g. Renewed for 12 months)"
                            className="min-h-0"
                          />
                        </div>
                      </div>
                    ) : (
                      <>
                        {r.renewalResponseAt && (
                          <p className={cn('text-xs mb-1', colors.textMuted)}>Response: {formatDate(r.renewalResponseAt)}</p>
                        )}
                        {r.renewalResponseNote && <p className={cn('text-sm', colors.text)}>{r.renewalResponseNote}</p>}
                      </>
                    )}
                  </Card>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <ConfirmationDialog
        isOpen={deleteConfirmId != null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete reminder"
        message="Delete this reminder record?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
      </>
    );
  }

  return (
    <>
      <Card variant="default" className="rounded-xl overflow-hidden">
        <div className={cn('flex items-center justify-between px-4 py-3 border-b', colors.border)}>
          <span className={cn('text-sm font-semibold', colors.text)}>Renewal reminders</span>
          {!adding && (
            <Button type="button" size="sm" onClick={() => setAdding(true)}>
              <PlusIcon className="w-4 h-4" />
              Log reminder
            </Button>
          )}
        </div>

        {adding && formBlock}

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className={cn('border-b', colors.border)}>
              <tr>
                <th className={cn('text-left py-3 px-3 font-semibold text-xs uppercase tracking-wide', colors.textMuted)}>Sent at</th>
                <th className={cn('text-left py-3 px-3 font-semibold text-xs uppercase tracking-wide', colors.textMuted)}>#</th>
                <th className={cn('text-left py-3 px-3 font-semibold text-xs uppercase tracking-wide', colors.textMuted)}>Channel</th>
                <th className={cn('text-left py-3 px-3 font-semibold text-xs uppercase tracking-wide', colors.textMuted)}>Response date</th>
                <th className={cn('text-left py-3 px-3 font-semibold text-xs uppercase tracking-wide', colors.textMuted)}>Response</th>
                <th className={cn('text-right py-3 px-3 font-semibold text-xs uppercase tracking-wide', colors.textMuted)}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {reminders.length === 0 && !adding && (
                <tr>
                  <td colSpan={6} className={cn('py-6 px-3 text-center text-sm', colors.textMuted)}>
                    No reminders logged. Click &quot;Log reminder&quot; when you send a renewal notification.
                  </td>
                </tr>
              )}
              {reminders.map((r) => (
                <tr key={r.id} className={cn('border-b', colors.border, colors.interactiveHover)}>
                  <td className={cn('py-2.5 px-3', colors.text)}>{formatDate(r.sentAt)}</td>
                  <td className={cn('py-2.5 px-3', colors.text)}>{r.reminderNumber ?? '—'}</td>
                  <td className={cn('py-2.5 px-3 capitalize', colors.text)}>{r.channel || '—'}</td>
                  <td className="py-2.5 px-3">
                    {editingId === r.id ? (
                      <DateInput
                        value={editForm.renewalResponseAt}
                        onChange={(e) => setEditForm((f) => ({ ...f, renewalResponseAt: e.target.value }))}
                        showClearButton={false}
                        className="min-w-[140px]"
                      />
                    ) : (
                      (r.renewalResponseAt && formatDate(r.renewalResponseAt)) || '—'
                    )}
                  </td>
                  <td className="py-2.5 px-3">
                    {editingId === r.id ? (
                      <Select
                        value={editForm.renewalResponseStatus}
                        onChange={(e) => setEditForm((f) => ({ ...f, renewalResponseStatus: e.target.value }))}
                        options={STATUS_SELECT_OPTIONS}
                        placeholder="Status"
                        className="min-w-[120px]"
                      />
                    ) : (
                      r.renewalResponseStatus ? renderRenewalStatusBadge(r.renewalResponseStatus) : '—'
                    )}
                  </td>
                  <td className="py-2.5 px-3 text-right">
                    {editingId === r.id ? (
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          type="button"
                          onClick={handleUpdate}
                          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="Save"
                          aria-label="Save"
                        >
                          <CheckIcon className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setEditingId(null)}
                          className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          title="Cancel"
                          aria-label="Cancel"
                        >
                          <XIcon className="w-5 h-5" />
                        </button>
                      </div>
                    ) : (
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          type="button"
                          onClick={() => openEdit(r)}
                          className="p-2 text-blue-600 hover:text-blue-900 hover:bg-blue-50 dark:text-blue-400 dark:hover:text-blue-300 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
                          title="Edit response"
                          aria-label="Edit response"
                        >
                          <PencilIcon className="w-5 h-5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDeleteClick(r.id)}
                          className="p-2 text-red-600 hover:text-red-900 hover:bg-red-50 dark:text-red-400 dark:hover:text-red-300 dark:hover:bg-red-900/30 rounded-lg transition-colors"
                          title="Delete"
                          aria-label="Delete"
                        >
                          <TrashIcon className="w-5 h-5" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {editingId != null && reminders.some((r) => r.id === editingId) && (
          <div className={cn('p-4 border-t', colors.border)}>
            <Label className="text-xs mb-1">Response note</Label>
            <Textarea
              value={editForm.renewalResponseNote}
              onChange={(e) => setEditForm((f) => ({ ...f, renewalResponseNote: e.target.value }))}
              rows={2}
              placeholder="Optional note (e.g. Renewed for 12 months)"
              className="min-h-0"
            />
          </div>
        )}
      </Card>

      <ConfirmationDialog
        isOpen={deleteConfirmId != null}
        onClose={() => setDeleteConfirmId(null)}
        onConfirm={handleDeleteConfirm}
        title="Delete reminder"
        message="Delete this reminder record?"
        confirmText="Delete"
        cancelText="Cancel"
        confirmVariant="danger"
      />
    </>
  );
};
